<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="680" scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card v-if="survey" class="res" dir="rtl">
      <!-- ===== header ===== -->
      <header class="res-head">
        <div class="res-head__text">
          <div class="res-head__line">
            <h2 class="res-title">תוצאות סקר #{{ survey.integer_id }}</h2>
            <span class="res-status" :class="'is-' + survey.status">
              <span class="res-dot"></span>{{ statusText }}
            </span>
          </div>
          <p v-if="survey.question" class="res-q">{{ survey.question }}</p>
        </div>
        <v-btn icon aria-label="סגירה" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </header>

      <v-progress-linear v-if="loadingVotes" indeterminate color="#0d2c6d" height="2" />

      <v-card-text class="res-body">
        <!-- ===== summary sentence ===== -->
        <div class="res-summary">
          <p class="res-summary__main">
            <strong>{{ totalVotes.toLocaleString('he-IL') }}</strong>
            {{ totalVotes === 1 ? 'הצבעה' : 'הצבעות' }}
            <template v-if="totalVotes">
              מ-<strong>{{ uniqueVoters.toLocaleString('he-IL') }}</strong>
              {{ uniqueVoters === 1 ? 'מצביע' : 'מצביעים' }}
            </template>
          </p>
          <p v-if="leadingOption" class="res-summary__lead">
            {{ tie ? 'יש תיקו בראש' : `מקש ${leadingOption.key} מוביל עם ${leadingOption.percent}%` }}
          </p>
          <v-btn class="res-export" outlined small color="#0d2c6d" :disabled="!totalVotes" @click="exportReport">
            <v-icon left small>mdi-download</v-icon>
            ייצוא לקובץ
          </v-btn>
        </div>

        <!-- ===== tabs ===== -->
        <div class="res-seg" role="tablist" aria-label="תצוגת תוצאות">
          <button type="button" role="tab" class="res-seg__btn" :class="{ 'is-active': activeTab === 0 }"
            :aria-selected="activeTab === 0 ? 'true' : 'false'" @click="activeTab = 0">לפי מקש</button>
          <button type="button" role="tab" class="res-seg__btn" :class="{ 'is-active': activeTab === 1 }"
            :aria-selected="activeTab === 1 ? 'true' : 'false'" @click="activeTab = 1">
            מצביעים
            <span class="res-seg__count">{{ uniqueVoters }}</span>
          </button>
        </div>

        <!-- ===== by key ===== -->
        <div v-if="activeTab === 0">
          <div v-if="!totalVotes && !loadingVotes" class="res-empty">
            <v-icon size="40" color="#b3bccd">mdi-dialpad</v-icon>
            <h3>עוד אין הצבעות</h3>
            <p>ההצבעות יופיעו כאן כשמתקשרים יצביעו בשלוחה 11.</p>
          </div>
          <ol v-else class="res-bars">
            <li v-for="item in sortedResults" :key="item.key" class="res-bar"
              :class="{ 'is-lead': leadingOption && item.count === leadingOption.count && item.count > 0 }">
              <span class="res-bar__key">{{ item.key }}</span>
              <span class="res-bar__track" :aria-label="`מקש ${item.key}: ${item.percent} אחוז`">
                <span class="res-bar__fill" :style="{ width: item.percent + '%' }"></span>
              </span>
              <span class="res-bar__pct">{{ item.percent }}%</span>
              <span class="res-bar__count">{{ item.count.toLocaleString('he-IL') }}</span>
            </li>
          </ol>
        </div>

        <!-- ===== voters ===== -->
        <div v-else>
          <p v-if="survey.anonymousVoting" class="res-note">
            <v-icon small color="#6b7690">mdi-incognito</v-icon>
            ההצבעה בסקר אנונימית. המצביעים מוצגים במספר סידורי בלבד.
          </p>
          <v-text-field v-model="voterSearch" class="res-search" placeholder="חיפוש לפי מספר טלפון או מקש"
            prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable />
          <div v-if="!filteredVoters.length && !loadingVotes" class="res-empty res-empty--small">
            <p>{{ totalVotes ? 'אין מצביעים שמתאימים לחיפוש.' : 'עוד אין מצביעים בסקר הזה.' }}</p>
          </div>
          <ul v-else class="res-voters">
            <li v-for="(row, i) in filteredVoters" :key="i" class="res-voter">
              <span class="res-voter__id">{{ row.identity }}</span>
              <span class="res-voter__key" :title="'הצביע במקש ' + row.optionKey">{{ row.optionKey }}</span>
              <span class="res-voter__when">
                <span class="res-voter__date">{{ formatDate(row.votedAt) }}</span>
                <span>{{ formatTime(row.votedAt) }}</span>
              </span>
              <span v-if="row.voteCount > 1" class="res-voter__multi">{{ row.voteCount }} הצבעות</span>
            </li>
          </ul>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiServices from '@/services/api.service';

export default {
  name: 'SurveyResultsDialog',
  props: {
    value: Boolean,
    survey: Object
  },
  data: () => ({
    votes: [], // נטענות מהשרת (אוסף ההצבעות), לא מתוך מסמך הסקר
    loadingVotes: false,
    activeTab: 0,
    voterSearch: '',
  }),
  computed: {
    statusText() {
      return { open: 'פתוח', processing: 'מתוזמן', closed: 'סגור' }[this.survey && this.survey.status] || '';
    },
    totalVotes() {
      return this.votes.length || 0;
    },
    uniqueVoters() {
      if (!this.votes.length) return 0;
      const unique = new Set();
      for (const v of this.votes) {
        unique.add(v.voter_hash || v.phoneNumber || 'unknown');
      }
      return unique.size;
    },
    sortedResults() {
      if (!this.survey?.options) return [];
      const counts = {};
      for (const opt of this.survey.options) {
        counts[opt.key] = 0;
      }
      for (const vote of this.votes) {
        if (counts[vote.optionKey] !== undefined) {
          counts[vote.optionKey]++;
        }
      }
      return Object.entries(counts)
        .map(([key, count]) => ({
          key: Number(key),
          count,
          percent: this.totalVotes > 0 ? Math.round((count / this.totalVotes) * 100) : 0
        }))
        .sort((a, b) => b.count - a.count || a.key - b.key);
    },
    leadingOption() {
      return this.sortedResults.length > 0 && this.sortedResults[0].count > 0
        ? this.sortedResults[0] : null;
    },
    tie() {
      return !!this.leadingOption && this.sortedResults.filter(r => r.count === this.leadingOption.count).length > 1;
    },
    votersList() {
      if (!this.votes.length) return [];
      const isAnon = this.survey.anonymousVoting;
      const votes = this.votes;

      // count votes per identity
      const countMap = {};
      for (const v of votes) {
        const id = isAnon ? (v.voter_hash || 'unknown') : (v.phoneNumber || 'unknown');
        countMap[id] = (countMap[id] || 0) + 1;
      }

      // build voter rows - one row per vote
      let anonCounter = 0;
      const anonMap = {};

      return votes.map(v => {
        const rawId = isAnon ? (v.voter_hash || 'unknown') : (v.phoneNumber || 'unknown');

        let displayId;
        if (isAnon) {
          if (!anonMap[rawId]) {
            anonCounter++;
            anonMap[rawId] = `מצביע ${anonCounter}`;
          }
          displayId = anonMap[rawId];
        } else {
          displayId = v.phoneNumber || 'מספר חסוי';
        }

        return {
          identity: displayId,
          isAnonymous: isAnon,
          optionKey: v.optionKey,
          votedAt: v.votedAt,
          voteCount: countMap[rawId],
          _rawId: rawId
        };
      }).sort((a, b) => new Date(b.votedAt) - new Date(a.votedAt));
    },
    filteredVoters() {
      const q = (this.voterSearch || '').trim();
      if (!q) return this.votersList;
      return this.votersList.filter(r => r.identity.includes(q) || String(r.optionKey) === q);
    },
  },
  watch: {
    survey: {
      immediate: true,
      handler(s) {
        this.votes = [];
        if (s && s._id) this.loadVotes(s._id);
      },
    },
    value(v) {
      if (v) {
        this.activeTab = 0;
        this.voterSearch = '';
      }
    }
  },
  methods: {
    async loadVotes(surveyId) {
      this.loadingVotes = true;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.VUE_APP_BASE_URL}/surveys/votes?surveyId=${surveyId}`, ApiServices.requestOptions('GET', '', token));
        if (res.status === 200) this.votes = (await res.json()).votes || [];
      } catch (e) {
        console.error('load survey votes failed', e);
      } finally {
        this.loadingVotes = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' });
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    },
    exportReport() {
      if (!this.survey) return;

      const BOM = '\uFEFF';

      // Header row
      let csv = BOM + 'מצביע,הצבעה (מקש),תאריך,שעה\n';

      // Use votersList for consistent display
      for (const row of this.votersList) {
        const date = this.formatDate(row.votedAt);
        const time = this.formatTime(row.votedAt);
        csv += `${row.identity},${row.optionKey},${date},${time}\n`;
      }

      // Add summary section
      csv += '\n--- סיכום ---\n';
      csv += `סה"כ הצבעות,${this.totalVotes}\n`;
      csv += `מצביעים ייחודיים,${this.uniqueVoters}\n\n`;
      csv += 'מקש,הצבעות,אחוז\n';
      for (const r of this.sortedResults) {
        csv += `${r.key},${r.count},${r.percent}%\n`;
      }

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `survey_${this.survey.integer_id}_results.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}
</script>

<style scoped>
.res {
  --ink: #0d2c6d;
  --ink-soft: #3d5488;
  --gold: #ffb91a;
  --line: #e3e8f2;
  --muted: #6b7690;
  font-family: 'Heebo', sans-serif;
  color: var(--ink);
  border-radius: 18px !important;
}

.res-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px 14px;
  border-bottom: 1px solid var(--line);
}
.res-head__line { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.res-title { font-size: 21px; font-weight: 800; margin: 0; }
.res-q { margin: 6px 0 0; font-size: 15px; color: var(--muted); }

.res-status { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: #8a93a6; }
.res-dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
.res-status.is-open { color: #2e9e5b; }
.res-status.is-processing { color: #e0920b; }

.res-body { padding: 20px 24px 26px !important; color: var(--ink) !important; }

/* summary */
.res-summary {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: "main export" "lead export";
  align-items: center;
  column-gap: 16px;
  margin-bottom: 20px;
}
.res-summary__main { grid-area: main; margin: 0; font-size: 16px; color: var(--ink-soft); }
.res-summary__main strong {
  font-size: 34px;
  font-weight: 800;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  vertical-align: -3px;
}
.res-summary__lead { grid-area: lead; margin: 6px 0 0; font-size: 14px; color: var(--muted); }
.res-export { grid-area: export; border-radius: 10px !important; font-weight: 700 !important; letter-spacing: 0 !important; }

/* tabs */
.res-seg { display: inline-flex; background: #e9edf5; border-radius: 12px; padding: 4px; gap: 2px; margin-bottom: 18px; }
.res-seg__btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 7px 16px; border-radius: 9px;
  font: 500 14px 'Heebo', sans-serif; color: var(--ink-soft);
}
.res-seg__btn.is-active { background: #fff; color: var(--ink); font-weight: 700; box-shadow: 0 1px 2px rgba(13, 44, 109, .12); }
.res-seg__count { font-size: 12px; font-weight: 700; color: var(--muted); font-variant-numeric: tabular-nums; }
.res-seg__btn:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }

/* bars */
.res-bars { list-style: none; padding: 0; margin: 0; display: grid; gap: 12px; }
.res-bar {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 52px 52px;
  align-items: center;
  gap: 12px;
}
.res-bar__key {
  width: 44px; height: 48px; border-radius: 11px;
  border: 1px solid #cfd7e6;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 19px; font-weight: 800;
}
.res-bar__track { height: 12px; border-radius: 6px; background: #edf1f7; overflow: hidden; }
.res-bar__fill { display: block; height: 100%; border-radius: 6px; background: var(--ink); transition: width .6s cubic-bezier(.2, .8, .2, 1); }
.res-bar__pct { font-size: 17px; font-weight: 800; text-align: left; font-variant-numeric: tabular-nums; }
.res-bar__count { font-size: 13px; color: var(--muted); text-align: left; font-variant-numeric: tabular-nums; }
.res-bar.is-lead .res-bar__key { border-color: var(--gold); background: rgba(255, 185, 26, .28); }
.res-bar.is-lead .res-bar__fill { background: var(--gold); }

/* voters */
.res-note { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--muted); margin: 0 0 12px; }
.res-search { margin-bottom: 10px !important; }
.res-search ::v-deep .v-input__slot { border-radius: 10px !important; }
.res-voters { list-style: none; padding: 0; margin: 0; max-height: 360px; overflow-y: auto; }
.res-voter {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px auto auto;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid var(--line);
  font-size: 14px;
}
.res-voter:last-child { border-bottom: none; }
.res-voter__id { font-weight: 600; font-variant-numeric: tabular-nums; direction: ltr; text-align: right; }
.res-voter__key {
  width: 30px; height: 32px; border-radius: 8px; border: 1px solid #cfd7e6;
  display: inline-flex; align-items: center; justify-content: center; font-weight: 800;
}
.res-voter__when { display: inline-flex; gap: 10px; color: var(--muted); font-size: 13px; font-variant-numeric: tabular-nums; }
.res-voter__when > span { unicode-bidi: isolate; }
.res-voter__multi { font-size: 12px; color: var(--ink-soft); background: #edf1f7; border-radius: 6px; padding: 2px 8px; }

/* empty */
.res-empty { text-align: center; padding: 36px 12px 20px; }
.res-empty h3 { font-size: 17px; font-weight: 800; margin: 10px 0 4px; }
.res-empty p { color: var(--muted); margin: 0; }
.res-empty--small { padding: 20px 12px; }

@media (max-width: 600px) {
  .res-summary { grid-template-columns: 1fr; grid-template-areas: "main" "lead" "export"; }
  .res-export { justify-self: start; margin-top: 12px; }
  .res-bar { grid-template-columns: 40px minmax(0, 1fr) 46px; }
  .res-bar__count { display: none; }
  .res-voter { grid-template-columns: minmax(0, 1fr) 34px; }
  .res-voter__when, .res-voter__multi { grid-column: 1 / -1; }
}
@media (prefers-reduced-motion: reduce) {
  .res-bar__fill { transition: none; }
}
</style>